import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, checkSupabaseConfig } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import LegalNotice from '../../components/LegalNotice';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AdvisorSignup = () => {
  const [formData, setFormData] = useState({
    firmName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const { setIsSigningUp, refreshProfile } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const config = checkSupabaseConfig();
    if (!config.valid) {
      setError(config.error);
      setLoading(false);
      return;
    }

    // Flag that we are in the signup process to prevent AuthContext from 
    // triggering "Missing Profile" redirects prematurely
    setIsSigningUp(true);

    try {
      // 1. Create Auth User
      console.log('Step 1: Starting Auth Signup for', formData.email);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        console.error('Step 1 Auth Error:', authError);
        throw authError;
      }
      
      const user = authData.user;
      if (!user) throw new Error("Authentication failed: No user returned.");

      // 2. Strict Session Check (Requirement for development)
      if (!authData.session) {
        console.error('Step 2 Error: Missing Session. Email confirmation is likely enabled.');
        throw new Error("Email confirmation appears to be enabled. Advisor signup requires immediate session creation during development. Please disable email confirmation or implement a server-side signup function.");
      }

      // 3. Call RPC function to complete signup
      console.log('Step 3: Calling complete_advisor_signup RPC');
      const { data: rpcData, error: rpcError } = await supabase.rpc('complete_advisor_signup', {
        p_firm_name: formData.firmName,
        p_first_name: formData.firstName,
        p_last_name: formData.lastName,
        p_email: formData.email
      });

      console.log('Step 3 RPC Response:', { rpcData, rpcError });

      if (rpcError) {
        console.error('Step 3 RPC Execution Error:', rpcError);
        throw new Error(`Account setup failed: ${rpcError.message}`);
      }

      // 4. Direct Database Verification (Bypassing local state/cache)
      console.log('Step 4: Direct Profile Verification for UID:', user.id);
      const { data: finalProfile, error: finalProfileError } = await supabase
        .from('profiles')
        .select('*, advisor_firms(*)')
        .eq('id', user.id)
        .single();

      if (finalProfileError || !finalProfile) {
        console.error('Step 4 Verification Error:', finalProfileError);
        console.log('Final Profile Data:', finalProfile);
        throw new Error("Advisor profile was not created correctly.");
      }

      // 5. Explicit Role & Link Validation
      console.log('Step 5: Validating Profile Integrity:', finalProfile);
      const isValidRole = finalProfile.role === 'advisor_admin' || finalProfile.role === 'advisor_user';
      if (!isValidRole || !finalProfile.advisor_firm_id) {
        console.error('Step 5 Validation Failed:', { 
          role: finalProfile.role, 
          firmId: finalProfile.advisor_firm_id 
        });
        throw new Error("Advisor profile was not created correctly.");
      }

      // 6. Sync Application Context State
      console.log('Step 6: Syncing AuthContext');
      await refreshProfile();

      console.log('Signup Flow Complete. Routing to dashboard.');
      setIsSigningUp(false);
      navigate('/dashboard');

    } catch (err) {
      console.error('Signup Failure Trace:', err);
      setError(err.message || "An unexpected error occurred.");
      setIsSigningUp(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
           <SafeIcon icon={FiIcons.FiBriefcase} className="mx-auto h-12 w-12 text-slate-800" />
          <h2 className="mt-6 text-3xl font-extrabold text-slate-900 tracking-tight">
            Advisor Registration
          </h2>
          <p className="mt-2 text-sm text-slate-600">Complete your firm profile</p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
            <form className="space-y-5" onSubmit={handleSignup}>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <div className="flex">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700">Firm Name</label>
                <input
                  name="firmName"
                  type="text"
                  required
                  value={formData.firmName}
                  onChange={handleChange}
                  className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                  placeholder="Acme Wealth Management"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">First Name</label>
                  <input
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Last Name</label>
                  <input
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Work Email</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <input
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <span className="flex items-center">
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                    Finishing Setup...
                  </span>
                ) : 'Create Advisor Account'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-slate-900 hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <LegalNotice />
    </div>
  );
};

export default AdvisorSignup;